// Shared renderer for the multi-point crossing PATHS, used by the Configure
// Lines editor and the Execute play screen so both look identical. Draws a
// single-colour glowing "gate" line with directional chevrons riding it
// (forward = start -> end), the control points, and — while drawing — a
// low-opacity preview of the line as it will look with the cursor as the next
// point.
import { bezierPoints } from './pathUtils'

const LINE_COLOR = '#f4813f'
const DIM_COLOR = 'rgba(244,129,63,0.4)'
const ARROW_COLOR = '#ffffff'

const smoothPx = (points, mapPt) => bezierPoints(points, 18).map(mapPt)

const polylineLength = (pts) => {
    let total = 0
    for (let i = 1; i < pts.length; i++) {
        total += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y)
    }
    return total
}

const drawChevron = (ctx, x, y, angle, color, size = 7) => {
    ctx.save()
    ctx.translate(x, y)
    ctx.rotate(angle)
    ctx.strokeStyle = color
    ctx.lineWidth = 3
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.beginPath()
    ctx.moveTo(-size, -size)
    ctx.lineTo(0, 0)
    ctx.lineTo(-size, size)
    ctx.stroke()
    ctx.restore()
}

// Walk the polyline placing chevrons every `spacing` px, up to `maxLen`.
const drawArrows = (ctx, pts, spacing, color, maxLen) => {
    let acc = 0
    let nextAt = spacing * 0.5
    for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1]
        const b = pts[i]
        const dx = b.x - a.x
        const dy = b.y - a.y
        const seg = Math.hypot(dx, dy)
        if (seg < 0.001) continue
        while (nextAt <= acc + seg && nextAt <= maxLen) {
            const t = (nextAt - acc) / seg
            drawChevron(
                ctx,
                a.x + dx * t,
                a.y + dy * t,
                Math.atan2(dy, dx),
                color
            )
            nextAt += spacing
        }
        acc += seg
        if (acc > maxLen) break
    }
}

// Stroke the polyline only up to `drawLen` px (for the draw-on animation).
const strokeUpTo = (ctx, pts, drawLen) => {
    ctx.beginPath()
    ctx.moveTo(pts[0].x, pts[0].y)
    let acc = 0
    for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1]
        const b = pts[i]
        const seg = Math.hypot(b.x - a.x, b.y - a.y)
        if (acc + seg <= drawLen) {
            ctx.lineTo(b.x, b.y)
            acc += seg
        } else {
            const t = seg > 0 ? (drawLen - acc) / seg : 0
            ctx.lineTo(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t)
            break
        }
    }
    ctx.stroke()
}

// paths: [{ id, name, points:[{x,y}] }] normalized. mapPt(n) -> {x,y} in px.
// opts: { activeId, ghostCursor, progress, showIndices, glow }
export function drawPaths(ctx, paths, mapPt, opts = {}) {
    const {
        activeId = null,
        ghostCursor = null,
        progress = 1,
        showIndices = false,
        glow = true,
    } = opts
    const prog = Math.max(0, Math.min(1, progress))

    ;(paths || []).forEach((path) => {
        const pts = path.points || []
        if (!pts.length) return
        const isActive = activeId == null || path.id === activeId
        const color = isActive ? LINE_COLOR : DIM_COLOR

        // Low-opacity preview of the whole line as it would look with the cursor
        // as the next point. Drawn first, so the committed (full-opacity) line
        // sits on top and only the tentative tail reads as faint preview.
        if (ghostCursor && isActive) {
            const preview = smoothPx([...pts, ghostCursor], mapPt)
            ctx.save()
            ctx.globalAlpha = 0.4
            ctx.strokeStyle = LINE_COLOR
            ctx.lineWidth = 5
            ctx.lineJoin = 'round'
            ctx.lineCap = 'round'
            ctx.beginPath()
            preview.forEach((p, i) =>
                i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)
            )
            ctx.stroke()
            const c = mapPt(ghostCursor)
            ctx.fillStyle = LINE_COLOR
            ctx.beginPath()
            ctx.arc(c.x, c.y, 6, 0, Math.PI * 2)
            ctx.fill()
            ctx.restore()
        }

        const smooth = smoothPx(pts, mapPt)
        if (smooth.length > 1) {
            const drawLen = polylineLength(smooth) * prog
            ctx.strokeStyle = color
            ctx.lineWidth = isActive ? 5 : 3
            ctx.lineJoin = 'round'
            ctx.lineCap = 'round'
            ctx.shadowBlur = glow && isActive ? 12 : 0
            ctx.shadowColor = LINE_COLOR
            strokeUpTo(ctx, smooth, drawLen)
            ctx.shadowBlur = 0
            if (isActive) drawArrows(ctx, smooth, 64, ARROW_COLOR, drawLen)
        }

        pts.forEach((n, i) => {
            const pt = mapPt(n)
            ctx.fillStyle = color
            ctx.beginPath()
            ctx.arc(pt.x, pt.y, isActive ? 6 : 5, 0, Math.PI * 2)
            ctx.fill()
            if (showIndices && isActive) {
                ctx.fillStyle = '#ffffff'
                ctx.font = '600 11px sans-serif'
                ctx.fillText(String(i + 1), pt.x + 8, pt.y - 8)
            }
        })
    })
}
